import React, { useState, useEffect } from 'react';
import { FaceLivenessDetector } from '@aws-amplify/ui-react-liveness';
import { ThemeProvider } from '@aws-amplify/ui-react';
import { Amplify } from 'aws-amplify';
import InlineCamera from './InlineCamera';
import VerificationLog from './VerificationLog';
import VerificationResults from './VerificationResults';
import SearchTransaction from './SearchTransaction';
import TransactionIDDisplay from './TransactionIDDisplay';
import { saveVerification, uploadImage } from './services/verificationService';
import { fetchLocationData } from './ipinfoService';
import config from './config';
import { useSettings } from './SettingsContext';
import SettingsPanel, { SettingsButton } from './SettingsPanel';
import MembershipStep from './MembershipStep';
import '@aws-amplify/ui-react/styles.css';
import './App.css';
import './InlineCamera.css';

// Configure Amplify with Cognito Identity Pool
Amplify.configure({
  Auth: {
    Cognito: {
      identityPoolId: 'us-east-1:09d5d355-cb05-4776-a1bb-dd6c06acb229',
      region: 'us-east-1',
      allowGuestAccess: true
    }
  }
});

// Use API endpoints from config
const API_CREATE_SESSION = 'https://u6hh8u4hjl.execute-api.us-east-1.amazonaws.com/default/CreateLivenessSession';
const API_GET_RESULTS = 'https://u6hh8u4hjl.execute-api.us-east-1.amazonaws.com/default/GetLivenessResults';
const API_EXTRACT_DOCUMENT = config.api.documentExtraction;
const API_GET_UPLOAD_URL = config.api.getUploadUrl;
const API_COMPARE_FACES = config.api.faceCompare;
const API_DETECT_SIGNATURE = config.api.signatureDetection;

function App() {
  const { settings } = useSettings();
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [livenessResults, setLivenessResults] = useState(null);
  const [faceCompareResults, setFaceCompareResults] = useState(null);
  const [error, setError] = useState(null);
  const [membershipData, setMembershipData] = useState(null);

  // Document capture states
  const [currentStep, setCurrentStep] = useState('start');
  const [frontImage, setFrontImage] = useState(null);
  const [backImage, setBackImage] = useState(null);
  const [frontS3Key, setFrontS3Key] = useState(null);
  const [backS3Key, setBackS3Key] = useState(null);
  const [s3Bucket, setS3Bucket] = useState(null);
  const [extractedData, setExtractedData] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [extractionPromise, setExtractionPromise] = useState(null);
  const [showCamera, setShowCamera] = useState(false);
  const [cameraSide, setCameraSide] = useState(null);
  const [signatureResults, setSignatureResults] = useState(null);
  const [livenessSkipped, setLivenessSkipped] = useState(false);
  
  // Search results state
  const [searchResults, setSearchResults] = useState(null);
  
  // Saved transaction ID (persisted before sessionId is cleared)
  const [savedTransactionId, setSavedTransactionId] = useState(null);
  const [isPersisted, setIsPersisted] = useState(false);

  // Individual loading states for parallel processing
  const [loadingStates, setLoadingStates] = useState({
    liveness: false,
    documentExtraction: false,
    faceComparison: false,
    signatureDetection: false
  });

  // Session tracking states
  const [sessionLog, setSessionLog] = useState([]);
  const [linkId] = useState(() => Math.floor(Math.random() * 9000) + 1000);

  // Capture initial page load session
  useEffect(() => {
    const captureInitialSession = async () => {
      const sessionData = await fetchLocationData();
      setSessionLog([sessionData]);
    };

    captureInitialSession();

    // Check URL path for /search route
    const path = window.location.pathname;
    if (path === '/search' || path === '/search/') {
      setCurrentStep('search');
    }
  }, []);

  // Persist verification once we land on results AND all async processing is complete.
  useEffect(() => {
    const persistVerification = async () => {
      if (currentStep !== 'results' || isPersisted || searchResults) {
        return;
      }

      // Wait until all async processing is done before persisting
      const stillLoading = loadingStates.liveness || loadingStates.documentExtraction || 
                           loadingStates.faceComparison || loadingStates.signatureDetection;
      if (stillLoading) {
        return;
      }

      const sourceSessionId = sessionId || livenessResults?.sessionId || null;
      if (!sourceSessionId) {
        return;
      }

      try {
        const response = await saveVerification({
          sessionId: sourceSessionId,
          livenessResults,
          documentData: extractedData,
          faceCompareResults,
          signatureResults,
          membershipData,
          frontS3Key,
          backS3Key,
          referenceImageS3Key: livenessResults?.referenceImage ? 'selfie' : null
        });

        const verificationId = response?.verificationId || sourceSessionId;
        setSavedTransactionId(verificationId);
        setIsPersisted(true);

        if (livenessResults?.referenceImage) {
          await uploadImage(verificationId, 'selfie', livenessResults.referenceImage);
        }
        if (frontImage) {
          await uploadImage(verificationId, 'front', frontImage);
        }
        if (backImage) {
          await uploadImage(verificationId, 'back', backImage);
        }
      } catch (err) {
        console.error('Failed to persist verification:', err);
        setError('Failed to save verification to PostgreSQL: ' + err.message);
      }
    };

    persistVerification();
  }, [
    currentStep,
    isPersisted,
    searchResults,
    sessionId,
    livenessResults,
    extractedData,
    faceCompareResults,
    signatureResults,
    membershipData,
    frontS3Key,
    backS3Key,
    frontImage,
    backImage,
    loadingStates
  ]);

  // Reset flow
  const resetFlow = () => {
    setCurrentStep('start');
    setFrontImage(null);
    setBackImage(null);
    setFrontS3Key(null);
    setBackS3Key(null);
    setS3Bucket(null);
    setExtractedData(null);
    setLivenessResults(null);
    setFaceCompareResults(null);
    setSignatureResults(null);
    setLivenessSkipped(false);
    setMembershipData(null);
    setError(null);
    setSessionId(null);
    setExtracting(false);
    setSearchResults(null);
    setSavedTransactionId(null);
    setIsPersisted(false);
    setExtractionPromise(null);
    setLoadingStates({
      liveness: false,
      documentExtraction: false,
      faceComparison: false,
      signatureDetection: false
    });
    // Don't reset sessionLog - keep the history
  };

  // Detect signature on ID documents
  const detectSignature = async (frontKey, backKey, bucket) => {
    try {
      console.log('Calling SignatureDetection API...');
      console.log('Front S3 Key:', frontKey);
      console.log('Back S3 Key:', backKey);
      console.log('Bucket:', bucket);

      const response = await fetch(API_DETECT_SIGNATURE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          frontS3Key: frontKey,
          backS3Key: backKey,
          bucket: bucket
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Signature detection error:', errorText);
        return null;
      }

      const data = await response.json();
      console.log('Signature detection response:', data);
      return data;
    } catch (error) {
      console.error('Signature detection error:', error);
      return null;
    }
  };

  // Compare faces using Rekognition (with S3 keys)
  const compareFaces = async (selfieS3Key, frontS3KeyParam, backS3KeyParam, bucketParam) => {
    try {
      console.log('Calling FaceCompare API with S3 keys...');
      console.log('Bucket:', bucketParam);
      console.log('Selfie S3 Key:', selfieS3Key);
      console.log('Front S3 Key:', frontS3KeyParam);
      console.log('Back S3 Key:', backS3KeyParam);

      const response = await fetch(API_COMPARE_FACES, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          selfieS3Key: selfieS3Key,
          frontS3Key: frontS3KeyParam,
          backS3Key: backS3KeyParam,
          bucket: bucketParam
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('FaceCompare error:', errorText);
        return null;
      }

      const data = await response.json();
      console.log('FaceCompare response:', data);

      return {
        frontMatch: data.frontMatch,
        backMatch: data.backMatch,
        bestMatch: {
          matched: data.matched,
          similarity: data.similarity,
          source: data.frontMatch?.similarity >= (data.backMatch?.similarity || 0) ? 'front' : 'back'
        },
        portraitImage: data.portraitImage
      };
    } catch (error) {
      console.error('Face comparison error:', error);
      return null;
    }
  };

  // Open inline camera
  const openCamera = (side) => {
    setCameraSide(side);
    setShowCamera(true);
  };

  // Handle camera capture
  const handleCameraCapture = (base64String) => {
    if (cameraSide === 'front') {
      setFrontImage(base64String);
    } else {
      setBackImage(base64String);
    }
    setShowCamera(false);
    setCameraSide(null);
  };

  // Close camera
  const closeCamera = () => {
    setShowCamera(false);
    setCameraSide(null);
  };

  // Upload image to S3 and return S3 key
  const uploadImageToS3 = async (base64Image, side) => {
    try {
      // Get presigned URL
      const urlResponse = await fetch(API_GET_UPLOAD_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ side })
      });

      if (!urlResponse.ok) {
        throw new Error('Failed to get upload URL');
      }

      const { uploadUrl, s3Key, bucket } = await urlResponse.json();

      // Convert base64 to blob
      const byteCharacters = atob(base64Image);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'image/jpeg' });

      // Upload to S3
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: blob,
        headers: { 'Content-Type': 'image/jpeg' }
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload image to S3');
      }

      return { s3Key, bucket };
    } catch (error) {
      console.error('S3 upload error:', error);
      throw error;
    }
  };

  // Start document extraction using S3
  const startDocumentExtraction = async (frontImg, backImg = null) => {
    setExtracting(true);
    setLoadingStates(prev => ({ ...prev, documentExtraction: true, signatureDetection: true }));

    const extractPromise = (async () => {
      try {
        // Upload front image to S3
        const { s3Key: uploadedFrontS3Key, bucket } = await uploadImageToS3(frontImg, 'front');

        // Save the S3 keys for face comparison later
        setFrontS3Key(uploadedFrontS3Key);
        setS3Bucket(bucket);

        // Upload back image to S3 if provided
        let uploadedBackS3Key = null;
        if (backImg) {
          const backResult = await uploadImageToS3(backImg, 'back');
          uploadedBackS3Key = backResult.s3Key;
          setBackS3Key(uploadedBackS3Key);
        }

        // Start signature detection immediately (runs in parallel with extraction)
        // Don't await - let it run in background
        detectSignature(uploadedFrontS3Key, uploadedBackS3Key, bucket)
          .then(sigResults => {
            if (sigResults) {
              setSignatureResults(sigResults);
              console.log('Signature detection completed early:', sigResults);
            }
          })
          .catch(err => console.error('Early signature detection error:', err))
          .finally(() => setLoadingStates(prev => ({ ...prev, signatureDetection: false })));

        // Call extraction Lambda with S3 keys
        const response = await fetch(API_EXTRACT_DOCUMENT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            frontS3Key: uploadedFrontS3Key,
            backS3Key: uploadedBackS3Key,
            bucket
          })
        });

        if (!response.ok) {
          throw new Error(`Server error: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();

        if (data.success) {
          // Return both extracted data and S3 info for face comparison
          return {
            ...data.extractedData,
            _s3Info: {
              frontS3Key: uploadedFrontS3Key,
              backS3Key: uploadedBackS3Key,
              bucket
            }
          };
        } else {
          throw new Error(data.error || 'Failed to extract document data');
        }
      } catch (error) {
        throw error;
      }
    })();

    setExtractionPromise(extractPromise);
    return extractPromise;
  };

  // Proceed after document capture
  const proceedToLiveness = async () => {
    if (!frontImage) {
      setError('Please capture front of document');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      startDocumentExtraction(frontImage, backImage);

      // Create liveness session
      const response = await fetch(API_CREATE_SESSION);
      const data = await response.json();

      if (data.sessionId) {
        setSessionId(data.sessionId);
        setCurrentStep('liveness');
      } else {
        setError('Failed to create liveness session');
      }
    } catch (err) {
      setError('Error starting verification: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle successful liveness check
  const handleAnalysisComplete = async () => {
    // Set loading states (signatureDetection already set in startDocumentExtraction)
    setLoadingStates(prev => ({
      ...prev,
      liveness: true,
      faceComparison: true
    }));

    // Don't set session ID as transaction ID — wait for the real verification_id from PostgreSQL.

    if (settings.membership?.enabled !== false) {
      setCurrentStep('membership');
    } else {
      setCurrentStep('results');
    }

    try {
      // Get liveness results (fast, do first)
      const livenessResponse = await fetch(API_GET_RESULTS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sessionId }),
      });

      const livenessData = await livenessResponse.json();
      setLivenessResults(livenessData);
      setLoadingStates(prev => ({ ...prev, liveness: false }));

      // Wait for document extraction to get S3 keys (needed for face comparison)
      let extractedDataResult = null;
      if (extractionPromise) {
        try {
          extractedDataResult = await extractionPromise;
          setExtractedData(extractedDataResult);
        } catch (err) {
          console.error('Document extraction error:', err);
          setError('Document extraction failed: ' + err.message);
        } finally {
          setExtracting(false);
          setLoadingStates(prev => ({ ...prev, documentExtraction: false }));
        }
      } else {
        setLoadingStates(prev => ({ ...prev, documentExtraction: false }));
      }

      // Upload selfie to S3 and compare faces (signature detection already started earlier)
      if (livenessData.referenceImage && extractedDataResult?._s3Info) {
        console.log('Uploading selfie to S3 for face comparison...');
        try {
          // Upload selfie to S3
          const { s3Key: selfieS3Key } = await uploadImageToS3(livenessData.referenceImage, 'selfie');
          console.log('Selfie uploaded to S3:', selfieS3Key);

          const { frontS3Key: fS3Key, backS3Key: bS3Key, bucket } = extractedDataResult._s3Info;

          // Run face comparison only (signature detection already running from startDocumentExtraction)
          try {
            const results = await compareFaces(selfieS3Key, fS3Key, bS3Key, bucket);
            console.log('Face comparison results:', results);
            if (results) {
              setFaceCompareResults(results);
              // If portrait was extracted, add it to extracted data
              if (results.portraitImage) {
                setExtractedData(prev => ({
                  ...prev,
                  portraitImage: results.portraitImage
                }));
              }
            }
          } catch (err) {
            console.error('Face comparison error:', err);
          } finally {
            setLoadingStates(prev => ({ ...prev, faceComparison: false }));
          }
        } catch (err) {
          console.error('Face comparison processing error:', err);
          setLoadingStates(prev => ({ ...prev, faceComparison: false }));
        }
      } else {
        // No images to process
        setLoadingStates(prev => ({ ...prev, faceComparison: false }));
      }

      // Capture verification complete session
      const completionSession = await fetchLocationData();
      completionSession.completed = true;
      setSessionLog(prev => [...prev, completionSession]);
    } catch (err) {
      setError('Error getting results: ' + err.message);
      // Reset all loading states on error
      setLoadingStates({
        liveness: false,
        documentExtraction: false,
        faceComparison: false,
        signatureDetection: false
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle errors
  const getReadableLivenessError = (error) => {
    if (!error) {
      return 'Unknown error (empty error object)';
    }

    if (typeof error === 'string') {
      return error;
    }

    // Amplify FaceLivenessDetector may pass { state: 'TIMEOUT' } or similar
    if (error.state) {
      return `${error.state}${error.message ? ': ' + error.message : ''}`;
    }

    if (error.message) {
      return error.message;
    }

    if (error.name) {
      return error.name;
    }

    try {
      const json = JSON.stringify(error, Object.getOwnPropertyNames(error));
      if (json && json !== '{}') return json;
    } catch (e) {
      // ignore
    }

    return String(error) || 'Unknown error';
  };

  const handleError = (error) => {
    const readableError = getReadableLivenessError(error);
    console.error('Liveness check error:', error, 'parsed:', readableError);
    setError('Liveness check failed: ' + readableError);
    setCurrentStep('start');
  };

  // Handle skip liveness
  const handleSkipLiveness = async () => {
    setLivenessSkipped(true);
    setLoadingStates(prev => ({
      ...prev,
      liveness: false,
      faceComparison: false
    }));

    // Check if membership is enabled
    if (settings.membership?.enabled !== false) {
      setCurrentStep('membership');
    } else {
      setCurrentStep('results');
    }

    // Wait for document extraction if still running
    if (extractionPromise) {
      try {
        const extractedDataResult = await extractionPromise;
        setExtractedData(extractedDataResult);
      } catch (err) {
        console.error('Document extraction error:', err);
      } finally {
        setExtracting(false);
        setLoadingStates(prev => ({ ...prev, documentExtraction: false }));
      }
    }

    // Capture verification complete session
    const completionSession = await fetchLocationData();
    completionSession.completed = true;
    completionSession.livenessSkipped = true;
    setSessionLog(prev => [...prev, completionSession]);
  };

  // Start verification flow
  const startVerification = () => {
    setCurrentStep('document-front');
  };

  const handleMembershipComplete = (data) => {
    setMembershipData(data);
    setCurrentStep('results');
  };

  return (
    <ThemeProvider>
      <div className="App">
        <SettingsPanel />
        <header className="App-header">
          <img src={settings.brand.logo} alt={settings.brand.logoAlt} className="logo" style={{ width: settings.brand.logoWidth }} />
          <h1>{settings.text.appTitle}</h1>
          <p>{settings.brand.tagline}</p>
        </header>

        <div className="container">
          {/* Progress Bar */}
          {currentStep !== 'start' && currentStep !== 'search' && currentStep !== 'searchResults' && (() => {
            const membershipEnabled = settings.membership?.enabled !== false;
            const isDocStep = currentStep === 'document-front' || currentStep === 'document-back';
            let stepNum = 1;
            return (
            <div className="progress-bar">
              <div className={`progress-step ${isDocStep ? 'active' : ''}`}>
                <div className="step-number">{stepNum++}</div>
                <div className="step-label">Document</div>
              </div>
              <div className="progress-line"></div>
              <div className={`progress-step ${currentStep === 'liveness' ? 'active' : ''}`}>
                <div className="step-number">{stepNum++}</div>
                <div className="step-label">Face Verification</div>
              </div>
              {membershipEnabled && (
                <>
                  <div className="progress-line"></div>
                  <div className={`progress-step ${currentStep === 'membership' ? 'active' : ''}`}>
                    <div className="step-number">{stepNum++}</div>
                    <div className="step-label">Membership</div>
                  </div>
                </>
              )}
              <div className="progress-line"></div>
              <div className={`progress-step ${currentStep === 'results' ? 'active' : ''}`}>
                <div className="step-number">{stepNum++}</div>
                <div className="step-label">Results</div>
              </div>
            </div>
            );
          })()}

          {/* Start Screen */}
          {currentStep === 'start' && (
            <div className="start-section">
              <div className="info-card">
                <h2>Welcome to {settings.brand.name}</h2>
                <p>Complete your identity verification in 3 simple steps:</p>
                <ul className="features-list">
                  <li>📸 Capture your ID document (front and back)</li>
                  <li>👤 Complete face liveness check</li>
                  <li>✓ Receive instant verification results</li>
                </ul>
              </div>
              <button
                onClick={startVerification}
                className="start-button"
              >
                Start Verification
              </button>

            </div>
          )}

          {/* Search Transaction */}
          {currentStep === 'search' && (
            <SearchTransaction
              onBack={() => setCurrentStep('start')}
              onSearchResults={(results) => {
                setSearchResults(results);
                setCurrentStep('searchResults');
              }}
            />
          )}

          {/* Search Results */}
          {currentStep === 'searchResults' && searchResults && (
            <div className="search-results-container">
              <button 
                className="back-button" 
                onClick={() => {
                  setCurrentStep('search');
                  setSearchResults(null);
                }}
              >
                ← Back to Search
              </button>

              <VerificationResults
                verificationId={searchResults.verificationId}
                livenessResults={searchResults.livenessResults}
                documentData={searchResults.documentData}
                faceCompareResults={searchResults.faceCompareResults}
                signatureResults={searchResults.signatureResults}
                membershipData={searchResults.membershipData}
                referenceImage={searchResults.referenceImage}
                frontImage={searchResults.frontImage}
                backImage={searchResults.backImage}
                frontS3Key={searchResults.frontS3Key}
                backS3Key={searchResults.backS3Key}
                status={searchResults.status}
                createdAt={searchResults.createdAt}
                isSearchResult={true}
              />

              <div style={{ marginTop: '20px', textAlign: 'center' }}>
                <button
                  onClick={() => {
                    setCurrentStep('start');
                    setSearchResults(null);
                  }}
                  className="action-button"
                >
                  Back to Home
                </button>
              </div>
            </div>
          )}

          {/* Document Capture - Front */}
          {currentStep === 'document-front' && (
            <div className="document-section">
              <div className="document-card">
                <h2>Step 1: Capture Front of ID</h2>
                <p className="step-instructions">
                  Use your camera to capture the front of your ID document
                </p>

                <div className="capture-box single-capture">
                  <h3>Front of ID <span className="required">*</span></h3>
                  {!frontImage ? (
                    <div className="camera-guide-simple">
                      <div className="camera-icon-large">📷</div>
                      <p className="camera-hint">Tap to open camera</p>
                      <button
                        className="open-camera-btn"
                        onClick={() => openCamera('front')}
                      >
                        Open Camera
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="image-preview">
                        <img src={`data:image/jpeg;base64,${frontImage}`} alt="Front of ID" />
                      </div>
                      <button
                        className="retake-button"
                        onClick={() => setFrontImage(null)}
                      >
                        🔄 Retake Photo
                      </button>
                    </>
                  )}
                </div>

                {frontImage && (
                  <button
                    onClick={() => setCurrentStep('document-back')}
                    className="action-button"
                  >
                    Continue
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Document Capture - Back */}
          {currentStep === 'document-back' && (
            <div className="document-section">
              <div className="document-card">
                <h2>Step 2: Capture Back of ID</h2>
                <p className="step-instructions">
                  Capture the back of your ID, or skip if not needed
                </p>

                <div className="capture-box single-capture">
                  <h3>Back of ID <span className="optional">(Optional)</span></h3>
                  {!backImage ? (
                    <div className="camera-guide-simple">
                      <div className="camera-icon-large">📷</div>
                      <p className="camera-hint">Tap to open camera</p>
                      <button
                        className="open-camera-btn"
                        onClick={() => openCamera('back')}
                      >
                        Open Camera
                      </button>
                    </div>
                  ) : (
                    <>
                      <div className="image-preview">
                        <img src={`data:image/jpeg;base64,${backImage}`} alt="Back of ID" />
                      </div>
                      <button
                        className="retake-button"
                        onClick={() => setBackImage(null)}
                      >
                        🔄 Retake Photo
                      </button>
                    </>
                  )}
                </div>

                <button
                  onClick={proceedToLiveness}
                  disabled={loading}
                  className="action-button"
                >
                  {loading ? 'Starting...' : 'Continue to Face Verification'}
                </button>
                {!backImage && (
                  <button
                    onClick={proceedToLiveness}
                    disabled={loading}
                    className="skip-button"
                    style={{ marginTop: '10px', background: 'transparent', color: '#6b7280', border: '1px solid #d1d5db', padding: '10px 24px', borderRadius: '6px', cursor: 'pointer', width: '100%' }}
                  >
                    Skip — No back needed
                  </button>
                )}
              </div>
            </div>
          )}

          {/* Face Liveness */}
          {currentStep === 'liveness' && sessionId && (
            <div className="liveness-section">
              <h3 className="step-title">Step 2: Face Liveness Verification</h3>
              {extracting && (
                <div className="processing-indicator">
                  <div className="spinner-small"></div>
                  <p>Processing document in background...</p>
                </div>
              )}
              <FaceLivenessDetector
                sessionId={sessionId}
                region="us-east-1"
                onAnalysisComplete={handleAnalysisComplete}
                onError={handleError}
                disableInstructionScreen={false}
              />

            </div>
          )}

          {/* Membership Step */}
          {currentStep === 'membership' && (
            <MembershipStep onComplete={handleMembershipComplete} />
          )}

          {/* Results */}
          {currentStep === 'results' && (
            <div className="results-section">
              {config.verification.showResultsAfterSelfie ? (
                <>
                  <VerificationResults
                    verificationId={sessionId || livenessResults?.sessionId}
                    livenessResults={livenessResults}
                    documentData={extractedData}
                    membershipData={membershipData}
                    faceCompareResults={faceCompareResults}
                    signatureResults={signatureResults}
                    referenceImage={livenessResults?.referenceImage}
                    frontImage={frontImage}
                    backImage={backImage}
                    frontS3Key={frontS3Key}
                    backS3Key={backS3Key}
                    s3Bucket={s3Bucket}
                    isProcessing={extracting}
                    loadingStates={loadingStates}
                    livenessSkipped={livenessSkipped}
                    onReset={resetFlow}
                  />

                  {/* Verification Log */}
                  <VerificationLog sessions={sessionLog} linkId={linkId} />
                </>
              ) : (
                <TransactionIDDisplay
                  verificationId={savedTransactionId || sessionId || livenessResults?.sessionId}
                  createdAt={new Date().toISOString()}
                  onReset={resetFlow}
                />
              )}
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="error-section">
              <div className="error-card">
                <h3>Verification Error</h3>
                <p className="error-message">{error}</p>
                <button
                  onClick={resetFlow}
                  className="action-button"
                >
                  Try Again
                </button>
              </div>
            </div>
          )}

          {/* Loading */}
          {loading && !sessionId && (
            <div className="loading-section">
              <div className="spinner"></div>
              <p>Processing...</p>
            </div>
          )}
        </div>

        <footer className="App-footer">
          <p>Verified securely by <strong>{settings.brand.name}</strong></p>
          <p className="footer-links">
            <a href="#" target="_blank" rel="noopener noreferrer">Contact Support</a>
            {' | '}
            <a href="#" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
          </p>
        </footer>
      </div>

      {/* Camera Overlay */}
      {showCamera && (
        <InlineCamera
          side={cameraSide}
          onCapture={handleCameraCapture}
          onClose={closeCamera}
        />
      )}
    </ThemeProvider>
  );
}

export default App;